import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatRelative } from "../lib/api";
import AppShell from "../components/AppShell";
import ShareBar from "../components/ShareBar";
import AuthorAvatar from "../components/AuthorAvatar";
import { useAuth } from "../lib/auth";
import { Newspaper, ArrowLeft, Globe, Tag, ArrowSquareOut, Eye, ChatCircleText, Trash, Star } from "@phosphor-icons/react";

export default function NewsDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [corridors, setCorridors] = useState([]);
  const [related, setRelated] = useState([]);
  const [comments, setComments] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const loadComments = async (newsSlug) => {
    try {
      const { data } = await api.get(`/news/${newsSlug}/comments`);
      setComments(data || []);
    } catch (_) { setComments([]); }
  };

  useEffect(() => {
    (async () => {
      const [a, c] = await Promise.all([
        api.get(`/news/${slug}`),
        api.get(`/news/corridors`),
      ]);
      setPost(a.data);
      setCorridors(c.data || []);
      const { data: more } = await api.get(`/news`, { params: { corridor: a.data.corridor, limit: 4 } });
      setRelated(more.filter((p) => p.slug !== a.data.slug).slice(0, 3));
      loadComments(a.data.slug);
    })().catch(() => {});
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!draft.trim() || busy) return;
    setBusy(true);
    try {
      await api.post(`/news/${slug}/comments`, { body: draft.trim() });
      setDraft("");
      await loadComments(slug);
    } catch (_) {}
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/news/comments/${id}`);
      await loadComments(slug);
    } catch (_) {}
  };

  if (!post) return <AppShell><div className="py-20 text-center label-mono"><span className="spinner mr-2 align-middle" />LOADING ARTICLE...</div></AppShell>;

  const c = corridors.find((x) => x.key === post.corridor) || { name: post.corridor, color: "#E60012" };
  const isDigest = post.kind === "digest";

  return (
    <AppShell>
      <div className="grid lg:grid-cols-12 gap-8" data-testid="news-detail-page">
        <article className="lg:col-span-8">
          <Link to="/news" className="btn btn-ghost btn-sm mb-6" data-testid="news-back-btn">
            <ArrowLeft size={12} weight="bold" /> All news
          </Link>

          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {isDigest ? (
              <span className="label-mono px-2 py-1 border flex items-center gap-1.5" style={{ color: "#E60012", borderColor: "#E60012" }} data-testid="news-digest-badge">
                <Star size={10} weight="fill" /> iAAM EDITORIAL DIGEST
              </span>
            ) : (
              <>
                <span className="hub-dot" style={{ color: c.color }} />
                <Link to={`/news?corridor=${post.corridor}`} className="label-mono hover:text-white" style={{ color: c.color }}>
                  {c.name.toUpperCase()}
                </Link>
              </>
            )}
            <span className="label-mono">·</span>
            <span className="label-mono flex items-center gap-1.5"><Globe size={10} weight="bold" /> {post.source_name.toUpperCase()}</span>
            <span className="label-mono">·</span>
            <span className="label-mono">{formatRelative(post.published_at)}</span>
            <span className="label-mono">·</span>
            <span className="label-mono flex items-center gap-1.5"><Eye size={10} weight="bold" /> {post.view_count} VIEW{post.view_count === 1 ? "" : "S"}</span>
          </div>

          <h1 className="font-display text-white mb-6" style={{ fontSize: "clamp(28px, 4vw, 48px)", fontWeight: 900, letterSpacing: "-0.035em", lineHeight: 1.05 }} data-testid="news-title">
            {post.title}
          </h1>

          {post.image_url && (
            <div className="border border-[#27272A] mb-8 bg-black overflow-hidden">
              <img
                src={post.image_url}
                alt=""
                className="w-full max-h-[420px] object-cover"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </div>
          )}

          {post.summary && (
            <div className="ai-panel p-5 mb-8" data-testid="news-summary">
              <div className="label-mono mb-2" style={{ color: "#E60012" }}>
                {isDigest ? "DIGEST OVERVIEW" : "IAAM EDITORIAL SUMMARY"}
              </div>
              <p className="text-zinc-100 leading-relaxed text-lg">{post.summary}</p>
            </div>
          )}

          <div className="prose-mobility text-zinc-200 leading-relaxed whitespace-pre-wrap text-lg mb-10" data-testid="news-body">
            {post.body}
          </div>

          {isDigest && post.featured_articles && post.featured_articles.length > 0 && (
            <div className="card p-5 mb-10" data-testid="news-digest-list">
              <div className="label-mono mb-3 flex items-center gap-1.5" style={{ color: "#E60012" }}>
                <Newspaper size={11} weight="fill" /> THIS WEEK'S TOP STORIES
              </div>
              <ol className="space-y-3 list-decimal list-inside text-sm">
                {post.featured_articles.map((a, i) => (
                  <li key={a.slug + i} className="text-zinc-300">
                    <Link to={`/news/${a.slug}`} className="text-white hover:underline underline-offset-4">{a.title}</Link>
                    <span className="text-zinc-500"> — {a.source_name}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <div className="mb-8" data-testid="news-share">
            <ShareBar title={post.title} summary={post.summary} />
          </div>

          {!isDigest && post.source_url && (
            <div className="border-t border-[#27272A] pt-6 flex flex-wrap items-center justify-between gap-3 mb-10" data-testid="news-source-attribution">
              <div className="text-sm text-zinc-400">
                <div className="label-mono mb-1">ORIGINAL SOURCE</div>
                <div className="text-white">{post.source_name}</div>
              </div>
              <a
                href={post.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary btn-sm"
                data-testid="news-source-link"
              >
                Read original <ArrowSquareOut size={12} weight="bold" />
              </a>
            </div>
          )}

          {/* Comments */}
          <section className="mt-10 border-t border-[#27272A] pt-8" data-testid="news-comments">
            <div className="flex items-center gap-2 mb-6">
              <ChatCircleText size={18} weight="light" style={{ color: "#E60012" }} />
              <h2 className="font-display text-white text-2xl" style={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
                {comments.length} {comments.length === 1 ? "comment" : "comments"}
              </h2>
            </div>

            {user && user.id ? (
              <form onSubmit={submit} className="mb-8" data-testid="news-comment-form">
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Add to the discussion..."
                  className="input resize-none mb-2"
                  data-testid="news-comment-input"
                />
                <div className="flex justify-between items-center">
                  <span className="label-mono">{draft.length} / 2000</span>
                  <button type="submit" disabled={!draft.trim() || busy} className="btn btn-primary btn-sm" data-testid="news-comment-submit">
                    {busy ? <span className="spinner" /> : "Post comment"}
                  </button>
                </div>
              </form>
            ) : (
              <div className="border border-dashed border-[#27272A] p-6 text-center mb-8">
                <p className="text-zinc-400 mb-3">Sign in to join the discussion.</p>
                <Link to="/login" className="btn btn-primary btn-sm">Sign in</Link>
              </div>
            )}

            <ul className="space-y-4">
              {comments.length === 0 ? (
                <li className="text-zinc-500 text-sm">No comments yet — be the first.</li>
              ) : comments.map((cm) => {
                const isAgent = cm.author_role === "agent";
                return (
                <li key={cm.id} className={`card p-4 ${isAgent ? "border-l-2" : ""}`} style={isAgent ? { borderLeftColor: cm.author_color || "#E60012" } : {}} data-testid={`news-comment-${cm.id}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <AuthorAvatar name={cm.author_name} avatarUrl={cm.author_avatar} size={28} bg={isAgent ? (cm.author_color || "#E60012") : "#27272A"} />
                    {isAgent ? (
                      <Link to={`/agent/${cm.author_id}`} className="text-sm text-white hover:underline underline-offset-4 flex items-center gap-2">
                        {cm.author_name}
                        <span className="label-mono px-1.5 py-0.5 border" style={{ color: cm.author_color || "#E60012", borderColor: cm.author_color || "#E60012", fontSize: 8 }}>AI · {(cm.author_specialty || "AGENT").toUpperCase()}</span>
                      </Link>
                    ) : (
                      <>
                        <Link to={`/profile/${cm.author_id}`} className="text-sm text-white hover:underline underline-offset-4">{cm.author_name}</Link>
                        {cm.author_role && cm.author_role !== "public" && (
                          <span className={`role-badge ${cm.author_role}`}>{cm.author_role}</span>
                        )}
                      </>
                    )}
                    <span className="label-mono">{formatRelative(cm.created_at)}</span>
                    {user && (user.id === cm.author_id || user.role === "admin") && !isAgent && (
                      <button onClick={() => remove(cm.id)} className="ml-auto btn btn-ghost btn-sm" title="Delete" data-testid={`news-comment-delete-${cm.id}`}>
                        <Trash size={11} />
                      </button>
                    )}
                  </div>
                  <p className="text-zinc-100 whitespace-pre-wrap leading-relaxed text-sm">{cm.body}</p>
                </li>
              );})}
            </ul>
          </section>
        </article>

        <aside className="lg:col-span-4 space-y-6">
          {post.tags && post.tags.length > 0 && (
            <div className="card p-5" data-testid="news-tags">
              <div className="label-mono mb-3 flex items-center gap-1.5"><Tag size={11} /> TAGS</div>
              <div className="flex flex-wrap gap-1.5">
                {post.tags.map((t) => (
                  <span key={t} className="label-mono px-2 py-1 border border-[#27272A]">{t.toUpperCase()}</span>
                ))}
              </div>
            </div>
          )}

          {related.length > 0 && (
            <div className="card p-5" data-testid="news-related">
              <div className="label-mono mb-3 flex items-center gap-1.5" style={{ color: c.color }}>
                <Newspaper size={11} weight="fill" /> MORE IN {c.name.toUpperCase()}
              </div>
              <ul className="space-y-3">
                {related.map((r) => (
                  <li key={r.id}>
                    <Link to={`/news/${r.slug}`} className="block group">
                      <div className="text-sm text-white group-hover:underline underline-offset-4 line-clamp-2 leading-snug mb-1">{r.title}</div>
                      <div className="label-mono">{r.source_name.toUpperCase()} · {formatRelative(r.published_at)}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </AppShell>
  );
}
